import {
  BaSeButton,
  BaSeCheckbox,
  BaSeInput,
  BaSeParagraph,
  BaSeSelect,
  BaSeTheme,
} from '@base/react';
import React, { useEffect } from 'react';
import { useCityController } from '../../../../hooks/controllers/city-controller';
import { useInterviewController } from '../../../../hooks/controllers/interview-controller';
import { useUfController } from '../../../../hooks/controllers/uf-controller';
import { useModule } from '../../../../hooks/module/module';
import { EMAIL_REGEX } from '../../../../utils/validators/constants';
import {
  ContentModal,
  ContentTitleModal,
  WrapperFields,
} from './interview-styled';

const INVALID_PHONE = 'Telefone inválido!';
const INVALID_EMAIL = 'E-mail inválido!';
const REQUIRED_FIELD = 'Campo obrigatório não informado!';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const NewCandidate = (props) => {
  const [formValidation, setFormValidation] = React.useState(false);
  const [interview, setInterview] = React.useState({});
  const { saveInterview, loading } = useInterviewController();
  const { getCitiesByUf, listCities } = useCityController();
  const { getUfs, listUfs } = useUfController();
  const [selected, setSelected] = React.useState({});
  const { moduleSelected, modules } = useModule();

  useEffect(() => {
    const moduleFinded = modules.find(
      (m) => m.id === props.candidate?.moduleSelect,
    );
    if (props.candidate?.moduleSelect && moduleFinded) {
      setSelected(moduleFinded);
      return;
    }
    if (moduleSelected !== undefined) {
      setSelected(moduleSelected);
    }
  }, [moduleSelected, modules, props]);

  const onValidateEmail = React.useCallback(() => {
    return EMAIL_REGEX.test(interview?.email);
  }, [interview.email]);

  const onValidatePhone = React.useCallback(() => {
    const clearPhone = interview.phone?.replace(/[^\d]/g, '');
    return clearPhone?.length >= 10;
  }, [interview.phone]);

  const onValidate = React.useCallback(() => {
    setFormValidation(true);
    if (
      !interview?.email ||
      !onValidateEmail() ||
      !interview.city ||
      interview.city?.id === 0 ||
      !interview.city?.uf ||
      interview.city?.uf?.id === 0 ||
      !interview?.phone ||
      !onValidatePhone() ||
      interview.candidate?.name?.length === 0 ||
      !interview.candidate?.name?.trim()
    ) {
      return false;
    }
    return true;
  }, [
    interview.candidate,
    interview.city,
    interview.email,
    interview.phone,
    onValidateEmail,
    onValidatePhone,
  ]);

  const save = React.useCallback(
    (option) => {
      if (!onValidate()) return;
      const nameFormated = interview.candidate.name.trim();
      const interviewWithModule = {
        ...interview,
        candidate: {
          ...interview.candidate,
          name: nameFormated,
        },
        module: { id: selected.id },
      };

      saveInterview(interviewWithModule).then((savedInterview) => {
        if (savedInterview) {
          return option === 'close'
            ? props.closeModal()
            : props.closeModal('new');
        }
      });
    },
    [interview, selected, onValidate, props, saveInterview],
  );

  React.useEffect(() => {
    if (props?.candidate) {
      setInterview({
        candidate: {
          cpf: props.candidate?.candidate?.cpf,
          name: props.candidate?.candidate?.name,
          socialName: props.candidate?.candidate?.socialName,
        },
        sendEmail: true,
      });
    } else {
      setInterview({
        candidate: {
          cpf: props.interview?.candidate?.cpf,
          name: props.interview?.candidate?.name,
          socialName: props.interview?.candidate?.socialName,
        },
        sendEmail: true,
      });
    }
    getUfs();
  }, [getUfs, props]);

  const returnStatusValidationName = React.useCallback(() => {
    if (!interview?.candidate?.name?.trim() && formValidation) {
      return 'invalid';
    }
    if (!interview?.candidate?.name?.trim()) {
      return 'normal';
    }
    return 'valid';
  }, [interview, formValidation]);


  const returnStatusValidationEmail = React.useCallback(() => {
    if (!interview?.email?.trim()) {
      return 'normal';
    }
    if (!onValidateEmail() || (!onValidateEmail() && formValidation)) {
      return 'invalid';
    }
    return 'valid';
  }, [interview, formValidation, onValidateEmail]);

  const returnStatusValidationPhone = React.useCallback(() => {
    if (!interview?.phone?.trim()) {
      return 'normal';
    }
    if (!onValidatePhone() || (!onValidatePhone() && formValidation)) {
      return 'invalid';
    }
    return 'valid';
  }, [onValidatePhone, interview, formValidation]);

  return (
    <>
      <ContentTitleModal bgColor={selected?.settingsMap?.primaryColor}>
        <BaSeParagraph
          isBold
          color={
            selected?.name === 'Empretec Startup'
              ? BaSeTheme.colors.institucionais.cinzaSebrae10
              : BaSeTheme.colors.defaultColors.white
          }
        >
          {selected.name}
        </BaSeParagraph>
      </ContentTitleModal>
      <ContentModal>
        <BaSeInput
          label="CPF:"
          placeholder="Informe o CPF"
          value={interview.candidate?.cpf}
          mask="cpf"
          inputMode="tel"
          isDisabled={true}
          onChange={(value) => {
            setInterview((actualInterview) => ({
              ...actualInterview,
              candidate: {
                ...actualInterview.candidate,
                cpf: value,
              },
            }));
          }}
        />
        <BaSeInput
          overrideColors={{
            complement: BaSeTheme.colors.feedbackInterface.erro35,
          }}
          label="Nome completo:"
          placeholder="Informe o nome completo"
          inputStatus={returnStatusValidationName()}
          complement={
            interview?.candidate?.name?.trim() === '' ||
              (formValidation && !interview?.candidate?.name)
              ? REQUIRED_FIELD
              : ''
          }
          value={interview.candidate?.name}
          isDisabled={props?.candidate.id ?? props?.candidate.name}
          onChange={(value) => {
            setInterview((actualInterview) => ({
              ...actualInterview,
              candidate: {
                ...actualInterview.candidate,
                name: value,
              },
            }));
          }}
        />

        <BaSeInput
          label="Nome social:"
          placeholder="Informe o nome social (opcional)"
          value={interview.candidate?.socialName}
          isDisabled={props?.candidate.id ?? props?.candidate.socialName}
          onChange={(value) => {
            setInterview((actualInterview) => ({
              ...actualInterview,
              candidate: {
                ...actualInterview.candidate,
                socialName: value,
              },
            }));
          }}
        />

        <BaSeInput
          label="E-mail:"
          placeholder="Informe o e-mail"
          overrideColors={{
            complement: BaSeTheme.colors.feedbackInterface.erro35,
          }}
          complement={
            formValidation && !interview.email
              ? REQUIRED_FIELD
              : formValidation && !onValidateEmail()
                ? INVALID_EMAIL
                : ''
          }
          typeInput="email"
          inputMode="email"
          value={interview?.email}
          inputStatus={returnStatusValidationEmail()}
          onChange={(value) => {
            setInterview((actualInterview) => ({
              ...actualInterview,
              email: value,
            }));
          }}
        />
        <BaSeInput
          label="Telefone:"
          placeholder="Informe o telefone"
          overrideColors={{
            complement: BaSeTheme.colors.feedbackInterface.erro35,
          }}
          complement={
            formValidation && !interview.phone
              ? REQUIRED_FIELD
              : formValidation && !onValidatePhone()
                ? INVALID_PHONE
                : ''
          }
          typeInput="tel"
          inputMode="tel"
          mask="phoneDDD"
          value={interview.phone}
          inputStatus={returnStatusValidationPhone()}
          onChange={(value) => {
            setInterview((actualInterview) => ({
              ...actualInterview,
              phone: value,
            }));
          }}
        />
        <WrapperFields>
          <BaSeSelect
            label="UF do seminário:"
            overrideColors={{
              complement: BaSeTheme.colors.feedbackInterface.erro35,
            }}
            color={BaSeTheme.colors.institucionais.cinzaSebrae10}
            complement={
              formValidation &&
                (!interview?.city?.uf || interview?.city?.uf?.id === 0)
                ? REQUIRED_FIELD
                : ''
            }
            selectedValueId={interview?.city?.uf?.id}
            values={listUfs}
            onChange={(value) => {
              setInterview((actualInterview) => ({
                ...actualInterview,
                city: {
                  uf: value,
                },
              }));
              getCitiesByUf(value);
            }}
          />
          <BaSeSelect
            label="Cidade:"
            color={BaSeTheme.colors.institucionais.cinzaSebrae10}
            complement={
              formValidation &&
                (!interview?.city?.id || interview?.city?.id === 0)
                ? REQUIRED_FIELD
                : ''
            }
            selectedValueId={interview?.city?.id}
            values={listCities?.[interview?.city?.uf?.id] || []}
            searchable
            onChange={(value) => {
              setInterview((actualInterview) => ({
                ...actualInterview,
                city: {
                  ...actualInterview.city,
                  id: value.id,
                },
              }));
            }}
          />
        </WrapperFields>
        {
          selected.id !== 4 && (
            <BaSeCheckbox
              color={BaSeTheme.colors.defaultColors.black}
              label="Enviar e-mail de inscrição"
              value={interview?.sendEmail}
              checked={interview?.sendEmail}
              onChange={(value) => {
                setInterview((actualInterview) => ({
                  ...actualInterview,
                  sendEmail: value,
                }));
              }}
              size="medium"
            />
          )
        }
        <WrapperFields>
          <BaSeButton
            onClick={() => save('close')}
            overrideColors={{
              foreground:
                selected.name === 'Empretec Startup'
                  ? BaSeTheme.colors.institucionais.cinzaSebrae10
                  : BaSeTheme.colors.defaultColors.white,
              backgroundColor: selected?.settingsMap?.primaryColor,
            }}
            type="primary"
            loadingText="Carregando..."
            isLoading={loading}
            value="Salvar e fechar"
            color={selected?.settingsMap?.primaryColor}
          />
          <BaSeButton
            onClick={() => save('new')}
            color={BaSeTheme.colors.defaultColors.black}
            type="secondary"
            loadingText="Carregando..."
            isLoading={loading}
            value="Salvar e criar novo"
          />
        </WrapperFields>
      </ContentModal>
    </>
  );
};
